import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Alert, Button, MenuItem, TextField,
  Grid, IconButton, Table, TableHead, TableRow, TableCell, TableBody,
  Chip, CircularProgress, Tooltip,
} from '@mui/material';
import { Home, Download, UploadFile, ImportExport, VpnKey } from '@mui/icons-material';
import axios from 'axios';

const API  = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const DataImportPage = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [collections, setCollections] = useState([]);
  const [collection,  setCollection]  = useState('');
  const [file,        setFile]        = useState(null);
  const [exporting,   setExporting]   = useState(false);
  const [importing,   setImporting]   = useState(false);
  const [error,       setError]       = useState('');
  const [result,      setResult]      = useState(null);

  const loadCollections = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/data-import/collections`, { headers: hdrs() });
      setCollections(data);
      if (data.length) setCollection(data[0].key);
    } catch {
      setError('Failed to load available collections');
    }
  }, []);

  useEffect(() => { loadCollections(); }, [loadCollections]);

  const handleExport = async () => {
    if (!collection) return;
    setExporting(true); setError('');
    try {
      const res = await axios.get(`${API}/api/data-import/${collection}/export`, {
        headers: hdrs(), responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `${collection}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch {
      setError('Export failed');
    } finally { setExporting(false); }
  };

  const handleImport = async () => {
    if (!collection || !file) return;
    setImporting(true); setError(''); setResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axios.post(`${API}/api/data-import/${collection}/import`, formData, {
        headers: { ...hdrs(), 'Content-Type': 'multipart/form-data' },
      });
      setResult(data);
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      setError(err.response?.data?.error || 'Import failed');
    } finally { setImporting(false); }
  };

  const active = collections.find(c => c.key === collection);
  const collectionLabel = active?.label || '';

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', mt: 4, mb: 4 }}>
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <ImportExport color="primary" />
              <Typography variant="h5" color="primary">Data Import / Export</Typography>
            </Box>
            <Tooltip title="Back to Setups">
              <IconButton onClick={() => navigate('/setups')} sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}>
                <Home />
              </IconButton>
            </Tooltip>
          </Box>

          <Alert severity="info" sx={{ mb: 3 }}>
            Export a collection to Excel, edit it, then re-import. Rows are matched by their key column —
            existing records are <strong>updated</strong>, new rows are <strong>added</strong>. Nothing is ever deleted.
          </Alert>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
            <Grid item xs={12} sm={5}>
              <TextField
                select label="Collection" value={collection}
                onChange={e => { setCollection(e.target.value); setResult(null); setError(''); }}
                fullWidth size="small"
              >
                {collections.map(c => <MenuItem key={c.key} value={c.key}>{c.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={7}>
              <Button
                variant="outlined" startIcon={exporting ? <CircularProgress size={16} /> : <Download />}
                onClick={handleExport} disabled={!collection || exporting}
              >
                {exporting ? 'Exporting…' : `Export ${collectionLabel} to Excel`}
              </Button>
            </Grid>
          </Grid>

          {active && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="overline" color="text.secondary" fontWeight={700}>
                Columns for {active.label}
              </Typography>
              <Box display="flex" gap={0.75} flexWrap="wrap" mt={0.5}>
                {active.columns.map(col => (
                  <Chip
                    key={col.header}
                    size="small"
                    label={col.required ? `${col.header} *` : col.header}
                    color={col.required ? 'primary' : 'default'}
                    variant={col.required ? 'filled' : 'outlined'}
                  />
                ))}
              </Box>
              <Typography variant="caption" color="text.secondary" display="block" mt={0.75}>
                <VpnKey sx={{ fontSize: 12, verticalAlign: 'text-bottom', mr: 0.3 }} />
                Key column: <strong>{active.columns.find(c => c.field === active.keyField)?.header || active.keyField}</strong>
                {active.keyAutoGenerated && ' — leave blank to auto-generate a new one'}.
                {' '}Filled-in chips (*) are required; the rest are optional.
              </Typography>
            </Box>
          )}

          <Typography variant="overline" color="text.secondary" fontWeight={700}>Import</Typography>
          <Grid container spacing={2} alignItems="center" sx={{ mt: 0.5, mb: 1 }}>
            <Grid item xs={12} sm={7}>
              <Button variant="outlined" component="label" startIcon={<UploadFile />} fullWidth>
                {file ? file.name : 'Choose Excel file…'}
                <input
                  ref={fileInputRef} type="file" hidden accept=".xlsx,.xls"
                  onChange={e => setFile(e.target.files?.[0] || null)}
                />
              </Button>
            </Grid>
            <Grid item xs={12} sm={5}>
              <Button
                variant="contained" fullWidth
                startIcon={importing ? <CircularProgress size={16} color="inherit" /> : <UploadFile />}
                onClick={handleImport} disabled={!file || importing}
              >
                {importing ? 'Importing…' : 'Import'}
              </Button>
            </Grid>
          </Grid>

          {result && (
            <Box sx={{ mt: 3 }}>
              <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
                <Chip label={`${result.totalRows} rows read`} size="small" />
                <Chip label={`${result.created} created`} size="small" color="success" />
                <Chip label={`${result.updated} updated`} size="small" color="info" />
                <Chip label={`${result.failed} failed`} size="small" color={result.failed ? 'error' : 'default'} />
              </Box>

              {result.errors.length > 0 && (
                <>
                  <Typography variant="subtitle2" color="error" gutterBottom>Row errors</Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell><strong>Row</strong></TableCell>
                        <TableCell><strong>Key</strong></TableCell>
                        <TableCell><strong>Error</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {result.errors.map((e, i) => (
                        <TableRow key={i}>
                          <TableCell>{e.row}</TableCell>
                          <TableCell>{e.key || '—'}</TableCell>
                          <TableCell>{e.error}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default DataImportPage;
